import { TaskDepartment, assignableRolesFor } from '../constants/taskDepartments';

/**
 * GP-1335 — who may appear in an "Assigned To" picker, and how their name reads.
 *
 * ONE source of truth for the option list behind every Assigned To control
 * (Billing Queue, Billing Item Troubleshooting, Order Troubleshooting, the
 * stage-queue Troubleshooting panels). Before this module each surface fed the
 * raw `/api/user-management/pharmacy/:id/users` response straight into an
 * Autocomplete, so the pickers offered service accounts ("PharmaGuide Support",
 * "Support support") next to real staff and rendered some options as truncated
 * raw e-mails (`ffarahani@nkshe…`) because those users have no name on file.
 *
 * Keep the rules HERE. Copying them into a second hook is how the list drifts
 * apart again.
 */

/** Shape shared by the pharmacy-users endpoint and the older ad-hoc fetches. */
export interface AssignableUserSource {
  email?: string;
  fullName?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phoxUserId?: string;
  customRole?: string;
}

export interface AssignableUserOption {
  id: string;
  name: string;
}

/**
 * Roles that are NOT offered as billing/troubleshooting assignees.
 *
 * `infusion_scheduler` is excluded per GP-1335: schedulers own the IV tabs, not
 * the general billing work queues, and they cluttered every picker. They stay in
 * the IV pickers — pass `{ includeInfusionSchedulers: true }` there — and this
 * changes only who is OFFERED, never what a scheduler is permitted to do (that
 * is still gated on the `editBilling` permission).
 */
export const NON_ASSIGNABLE_ROLES = new Set(['infusion_scheduler']);

/**
 * Non-human accounts. Matched on the e-mail local part (the stable part of the
 * identity) rather than the display name, so renaming the account in Mongo does
 * not silently put it back in the picker.
 *
 * Release 2138 review: the list is deliberately NARROW — only mailboxes that
 * are unambiguously not a person (delivery robots, the platform's own vendor
 * accounts). Role-flavoured mailboxes (`admin@`, `it@`, `support@`, `qa@`,
 * `info@`, `test@`, `demo@` …) were removed: a site may legitimately assign
 * work to them, and the old token-splitting/digit-stripping matcher was hiding
 * REAL staff (`it.jane@`, `admin.smith@`, `qa.lead@`) from every picker.
 */
const SERVICE_ACCOUNT_LOCAL_PARTS = new Set([
  'noreply',
  'no-reply',
  'donotreply',
  'do-not-reply',
  'system',
  'sysadmin',
  'service',
  'automation',
  'bot',
  'pharmaguide',
  'phox',
  'phoxsupport',
  'pharmaguidesupport',
]);

const emailLocalPart = (email?: string): string =>
  (email || '').split('@')[0]!.trim().toLowerCase();

/**
 * True for shared/robot logins that must never be handed a patient's work.
 *
 * EXACT local-part matches only. No token splitting and no digit stripping —
 * those "same account, tagged inbox" heuristics also matched people
 * (`it.jane@` → `it`, `admin.smith@` → `admin`) and made them unassignable.
 */
export function isServiceAccount(user: AssignableUserSource): boolean {
  const local = emailLocalPart(user.email);
  if (!local) return false;
  return SERVICE_ACCOUNT_LOCAL_PARTS.has(local);
}

/**
 * Human-readable label for a user, so a picker never shows a raw e-mail.
 * fullName → "first last" → the e-mail local part title-cased ("ffarahani" →
 * "Ffarahani"). Users with no name on file are the reason the last step exists;
 * fixing their Mongo record is the real fix, but the picker must stay readable.
 */
export function formatUserDisplayName(user: AssignableUserSource): string {
  const fullName = (user.fullName || user.name || '').trim();
  if (fullName) return fullName;

  const composed = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  if (composed) return composed;

  const local = emailLocalPart(user.email);
  if (!local) return user.phoxUserId || 'Unknown user';

  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Turn a stored identity (e-mail or Auth0 id) into the name a grid cell shows.
 *
 * The roster map first — it is built from the page's ONE
 * `/api/user-management/pharmacy/:id/users` fetch, so this is a lookup, never a
 * request. Then the same e-mail → display-name rule the pickers use, so a user
 * outside the fetched roster still reads as a name instead of a truncated raw
 * e-mail (GP-1335). Falls back to the identity itself rather than inventing one.
 *
 * Shared by the Assigned To picker and the read-only "Sent back by" column
 * (GP-1294) so the two columns can never disagree about how a person's name is
 * spelled.
 */
export function resolveUserDisplayName(
  identity: string | null | undefined,
  userIdToNameMap?: Map<string, string>,
): string {
  if (!identity) return '';
  const mapped = userIdToNameMap?.get(identity);
  if (mapped) return mapped;
  if (identity.includes('@')) return formatUserDisplayName({ email: identity });
  return identity;
}

/** True when this user may be offered as an assignee. */
export function isAssignableUser(
  user: AssignableUserSource,
  options: { includeInfusionSchedulers?: boolean } = {},
): boolean {
  if (!user.email) return false;
  if (isServiceAccount(user)) return false;
  if (
    !options.includeInfusionSchedulers &&
    user.customRole &&
    NON_ASSIGNABLE_ROLES.has(user.customRole)
  ) {
    return false;
  }
  return true;
}

/**
 * The picker's options: assignable users only, every one labelled with a display
 * name, sorted alphabetically. `id` is the e-mail because that is what
 * `assignedTo` stores.
 */
export function toAssignableOptions(
  users: AssignableUserSource[],
  options: { includeInfusionSchedulers?: boolean } = {},
): AssignableUserOption[] {
  return users
    .filter((user) => isAssignableUser(user, options))
    .map((user) => ({ id: user.email as string, name: formatUserDisplayName(user) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * PRX-67 (wave 1 review item 1b, Major) — the users assignable to a Task
 * Queue task in a given department (D7's role gate — mirrored from the
 * backend's TASK_ASSIGNEE_ROLES_BY_DEPARTMENT via assignableRolesFor).
 *
 * ONE source: the queue's list view / bulk picker (TaskQueueContent) and the
 * create/edit dialog (CreateTaskDialog) both call this instead of each
 * keeping its own department-role filter — the create/edit dialog used to
 * offer every pharmacy user regardless of department, including roles the
 * server would reject (e.g. a logistics driver on a billing task).
 *
 * `department` null/undefined offers the union of every department's roles
 * (`assignableRolesFor(null)`) — a department-less task, or a caller's own
 * "no department yet" fallback list.
 */
export function getDepartmentAssignableUsers(
  users: AssignableUserSource[] | undefined,
  department?: TaskDepartment | null,
): AssignableUserOption[] {
  const allowed = new Set(assignableRolesFor(department));
  return (users || [])
    .filter((u) => u.customRole && allowed.has(u.customRole) && u.email)
    .map((u) => ({ id: u.email as string, name: u.fullName || (u.email as string) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * id → display name for EVERY fetched user (including the ones excluded from the
 * picker). An item already assigned to an infusion scheduler or a service
 * account still has to render as a name in the grid, not as a raw e-mail.
 */
export function buildUserIdToNameMap(users: AssignableUserSource[]): Map<string, string> {
  const map = new Map<string, string>();
  users.forEach((user) => {
    const displayName = formatUserDisplayName(user);
    if (user.email) map.set(user.email, displayName);
    if (user.phoxUserId) {
      map.set(user.phoxUserId, displayName);
      map.set(`auth0|${user.phoxUserId}`, displayName);
      map.set(`auth0|${user.phoxUserId}@auth0.local`, displayName);
    }
  });
  return map;
}
